/**
 * THE WALK AS A SHAPE — the bent line the steps screen draws instead of a map.
 *
 * ── ⚠️ IT IS PROPORTIONED, NOT TO SCALE, AND THAT IS THE WHOLE IDEA ───────────────
 * A true-to-scale drawing of "turn left 22 m, turn left 6 m, straight 108 m" is a long
 * stroke with two invisible kinks at one end — the two turns a passenger actually has to
 * make are the part that vanishes. The square root below is what fixes that: it compresses
 * the long leg and lifts the short ones, so every corner in the recorded walk is a corner
 * you can see. Someone reading this needs the SHAPE of the route, not its dimensions; the
 * metres are printed on the instruction card underneath.
 *
 * ── ⚠️ AND IT IS NOT DERIVED FROM THE ROUTING GRIDS ───────────────────────────────
 * The angles come from the product owner's own on-site recorded walk, the same source as
 * the words on the card. They are not expected to agree with a solved route turn for turn,
 * and "correcting" one against the other would put the picture and the instruction beside
 * it out of step.
 */

/**
 * Segment length in pixels, from the recorded distance.
 *
 * ⚠️ THE FLOOR MATTERS AS MUCH AS THE CURVE. A recorded 0 m step — the generic fallback
 * plan has exactly one — would otherwise be a zero-length segment, i.e. no line at all on
 * a screen whose whole content is a line. 36 px is short enough to read as "barely any
 * distance" and long enough to exist.
 */
const BASE_LENGTH = 36
const LENGTH_PER_ROOT_METRE = 14

/** Breathing room around the path, so a marker at either end is never clipped. */
export const ILLUSTRATION_PADDING = 28

/**
 * The path, as points in a padded local coordinate box.
 *
 * ⚠️ HEADING TURNS FIRST, THEN THE SEGMENT IS DRAWN. A step's `angle` is the turn you make
 * BEFORE walking it — "turn left, then 22 m" — so applying it after would draw every leg
 * one instruction behind the words.
 *
 * ⚠️ 0° IS SCREEN UP, WHICH IS WHY Y IS SUBTRACTED. Screen y grows downward and the
 * recorded angles are compass-style around "ahead"; taking `-cos` is what keeps "straight
 * on" pointing up the page instead of down it.
 *
 * @param {Array<{angle: number, distanceMeters: number}>} steps
 * @returns {{points: Array<{x: number, y: number}>, width: number, height: number}}
 */
export function buildWalkIllustration(steps) {
    const raw = [{x: 0, y: 0}]
    let heading = 0

    for (const step of steps || []) {
        heading += Number(step?.angle) || 0
        const metres = Math.max(0, Number(step?.distanceMeters) || 0)
        const length = BASE_LENGTH + Math.sqrt(metres) * LENGTH_PER_ROOT_METRE

        const radians = (heading * Math.PI) / 180
        const last = raw[raw.length - 1]
        raw.push({
            x: last.x + Math.sin(radians) * length,
            y: last.y - Math.cos(radians) * length,
        })
    }

    /* Shifted so the box starts at the padding rather than wherever the walk happened to
       wander — the caller scales this into a stage and cannot do that against negatives. */
    const xs = raw.map((p) => p.x)
    const ys = raw.map((p) => p.y)
    const minX = Math.min(...xs)
    const minY = Math.min(...ys)

    return {
        points: raw.map((p) => ({
            x: p.x - minX + ILLUSTRATION_PADDING,
            y: p.y - minY + ILLUSTRATION_PADDING,
        })),
        width: Math.max(...xs) - minX + ILLUSTRATION_PADDING * 2,
        height: Math.max(...ys) - minY + ILLUSTRATION_PADDING * 2,
    }
}

/**
 * How long the line takes to grow across one segment.
 *
 * ⚠️ IT IS PACED AT WALKING SPEED, NOT AT A FIXED DURATION, so the animation says
 * something: a 6 m hop snaps and a 279 m leg unrolls. A constant duration would draw those
 * two identically and the picture would stop carrying any sense of distance at all.
 *
 * ⚠️ CAPPED, THOUGH, BECAUSE 279 m AT WALKING PACE IS THREE AND A HALF MINUTES. Nobody
 * watches a line for that long; past the cap it has stopped being feedback and become a
 * screen that looks frozen.
 */
const WALK_METRES_PER_SECOND = 1.35
const MIN_DRAW_MS = 700
const MAX_DRAW_MS = 4000

export function illustrationDrawMs(distanceMeters) {
    const metres = Math.max(0, Number(distanceMeters) || 0)
    return Math.min(MAX_DRAW_MS, Math.max(MIN_DRAW_MS, (metres / WALK_METRES_PER_SECOND) * 1000))
}

export default buildWalkIllustration
